import React from "react";

/**
 * My Savvy — official brand lockup.
 *
 * Renders the transparent-background PNG supplied by the owner. The mark is a
 * pair of stacked cyan/blue flames with the "MY SAVVY" wordmark set in dark
 * glyphs. To use on dark surfaces we flip the black-to-white while preserving
 * the blue flames via `invert(1) hue-rotate(180deg)`.
 *
 * Variants:
 *   - "light":     dark wordmark (on cream / white bg)
 *   - "dark":      white wordmark (on graphite / photo bg)
 *   - "amber":     dark wordmark (on amber)
 *   - "onSidebar": white wordmark (sidebar)
 *
 * Sizing: `size` controls the overall height in pixels. The source aspect is
 * ~3.17:1 (571×180) so width is derived automatically.
 */
const LOGO_SRC =
  "https://customer-assets.emergentagent.com/job_construct-hub-357/artifacts/2ai6a6zs_my-lg.png";

export default function SavvyLogo({
  variant = "light",
  size = 40,
  showTagline = false,
  tagline = "Construction Updates OS",
  testId,
}) {
  const isDark = variant === "dark" || variant === "onSidebar";

  const imgStyle = {
    height: size,
    width: "auto",
    display: "block",
    // On dark surfaces we invert (black wordmark → white) and hue-rotate 180°
    // which visually restores the original flame blues (invert flips colours,
    // hue-rotate 180° flips them back while leaving greyscale inverted).
    filter: isDark ? "invert(1) hue-rotate(180deg)" : "none",
  };

  const taglineColor = isDark ? "rgba(255,255,255,0.55)" : "#78716C";

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <img
        src={LOGO_SRC}
        alt="My Savvy"
        style={imgStyle}
        draggable={false}
      />
      {showTagline && (
        <div
          className="leading-none"
          style={{
            paddingLeft: "0.75rem",
            borderLeft: isDark
              ? "1px solid rgba(255,255,255,0.18)"
              : "1px solid rgba(28,25,23,0.15)",
          }}
        >
          <div
            className="font-mono uppercase"
            style={{
              color: taglineColor,
              fontSize: Math.max(size * 0.26, 10),
              letterSpacing: "0.22em",
              fontWeight: 500,
            }}
          >
            {tagline}
          </div>
        </div>
      )}
    </div>
  );
}
